import styled, { css } from 'styled-components';
import { applyStyleModifiers, type ModifiersConfig, type ModifierKeys } from 'styled-components-modifiers';
import { rgba } from 'polished';

// Import styles
import { customMinResponsive, size } from 'components/styles';

// Import types
import { Modifiers } from 'types/views/styles';

const CIRCLE_SIZE = '1.5rem';
const CIRCLE_MARGIN = '0.3rem';

const pseudoElementStyles = ({ size = '100%', radius = '50%', color = '' }) => css`
	content: '';
	display: inline-block;
	width: ${size};
	height: ${size};
	border-radius: ${radius};
	background: ${color};
	transition: 0.2s ease-in-out;
`;

const INPUT_MODIFIERS: Modifiers = {
	light: ({ theme }) => css`
		&::after {
			${pseudoElementStyles({ radius: '1.6rem', color: rgba(theme.palette.black, 0.2) })}
		}
	`
};

const CUSTOM_ERROR_MODIFIERS: Modifiers = {
	light: ({ theme }) => css`
		color: ${theme.palette.errorLight};
	`
};

const REQUIRED_MARK_MODIFIERS: Modifiers = {
	light: ({ theme, isOptional }) => css`
		color: ${isOptional ? 'inherit' : theme.palette.errorLight};
	`
};

const CONTENT_MODIFIERS: Modifiers = {
	small: ({ theme }) => css`
		color: ${rgba(theme.palette.secondary.main, 0.8)};
		font-size: 1.3rem;
		font-style: normal;

		${customMinResponsive(
			size.semi,
			`
			font-size: 1.4rem;
		`
		)}
	`,
	light: ({ theme, indented }) => css`
		color: ${indented ? rgba(theme.palette.black, 0.65) : rgba(theme.palette.black, 0.75)};
		font-size: ${indented ? 1.4 : 1.5}rem !important;
		line-height: ${indented ? 1.4 : 1.5} !important;
		font-weight: ${theme.font.normal};

		span {
			font-weight: inherit !important;
		}

		a {
			color: inherit;
			text-decoration: underline;

			&:hover {
				color: ${rgba(theme.palette.black, 0.8)};
				text-decoration: none;
			}
		}
	`,
	invoice: ({ theme }) => css`
		font-size: 1.6rem;
		font-weight: ${theme.font.normal};
	`
};

export const Wrapper = styled.div<{ customError?: boolean }>`
	padding-bottom: ${({ customError }) => (customError ? 1.5 : 0)}rem;
	position: relative;
`;

export const Label = styled.label<{ disabled?: boolean }>`
	display: grid;
	grid-template-columns: 1.8rem auto;
	column-gap: 1.2rem;
	opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

export const Input = styled.input<{ modifiers?: ModifierKeys }>`
	position: relative;
	width: 3.2rem;
	height: 2.1rem;
	cursor: pointer;
	appearance: initial;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	&::before {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		left: ${CIRCLE_MARGIN};
		${({ theme }) => pseudoElementStyles({ size: CIRCLE_SIZE, color: theme.palette.secondary.main })}
	}

	&::after {
		${({ theme }) => pseudoElementStyles({ radius: '1.6rem', color: rgba(theme.palette.white, 0.4) })}
	}

	&:checked {
		&::before {
			left: calc(100% - ${CIRCLE_SIZE} - ${CIRCLE_MARGIN});
		}

		&::after {
			background: ${({ theme }) => theme.palette.primary.main};
		}
	}

	${applyStyleModifiers(INPUT_MODIFIERS as ModifiersConfig)};
`;

export const Content = styled.div<{ indented: boolean; modifiers?: ModifierKeys }>`
	color: ${({ theme }) => theme.palette.secondary.main};
	font-size: 1.4rem;
	cursor: pointer;
	line-height: 1.4em;
	padding-left: 1.2rem;

	span {
		font-weight: ${({ theme }) => theme.font.light} !important;
	}

	a {
		color: ${({ theme }) => theme.palette.primary.main};

		&:hover {
			color: ${({ theme }) => rgba(theme.palette.secondary.main, 0.8)};
		}
	}

	${customMinResponsive(
		size.semi,
		css`
			font-size: 1.6rem;
		`
	)}

	${applyStyleModifiers(CONTENT_MODIFIERS as ModifiersConfig)};
`;

export const CustomError = styled.p<{ modifiers?: ModifierKeys }>`
	position: absolute;
	font-size: 1.2rem;
	color: ${({ theme }) => theme.palette.error};
	line-height: 1.8rem;

	${applyStyleModifiers(CUSTOM_ERROR_MODIFIERS as ModifiersConfig)};
`;

export const RequiredMark = styled.p<{ isOptional?: boolean; modifiers?: ModifierKeys }>`
	color: ${({ theme, isOptional = false }) => (isOptional ? 'inherit' : theme.palette.errorLight)};
	display: inline;
	font-weight: inherit;
	margin-right: 0.2em;

	${applyStyleModifiers(REQUIRED_MARK_MODIFIERS as ModifiersConfig)};
`;
